(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const API = 'https://api.judge.me/reviews/reviews_for_widget';
  const REVIEW_POST = 'https://api.judge.me/api/v1/reviews';
  const PER_PAGE = 100;
  const MAX_PHOTOS = 3;
  const MAX_FILE_MB = 25;

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starsHtml(rating, partial) {
    const value = Number(rating) || 0;
    const full = Math.floor(value);
    const hasHalf = !partial && value - full >= 0.4 && value - full < 0.9;
    let out = '';
    for (let i = 1; i <= 5; i++) {
      let cls = 'tt-rev__star';
      if (i <= full) cls += ' is-on';
      else if (i === full + 1 && hasHalf) cls += ' is-half';
      out += `<span class="${cls}" aria-hidden="true">★</span>`;
    }
    return `<div class="tt-rev__stars" role="img" aria-label="${value.toFixed(1)} out of 5 stars">${out}</div>`;
  }

  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (_e) {
      return '';
    }
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return (tmp.textContent || tmp.innerText || '').trim();
  }

  function mediaHtml(review) {
    const pics = Array.isArray(review.pictures_urls) ? review.pictures_urls.filter(Boolean) : [];
    const videos = Array.isArray(review.video_external_ids) ? review.video_external_ids.filter(Boolean) : [];
    if (!pics.length && !videos.length) return '';

    const items = [];
    pics.slice(0, 4).forEach((url) => {
      items.push(
        `<a class="tt-rev__media-item" href="${escapeHtml(url)}" target="_blank" rel="noopener"><img src="${escapeHtml(url)}" alt="" loading="lazy"></a>`
      );
    });
    videos.slice(0, 1).forEach((id) => {
      items.push(
        `<div class="tt-rev__media-item tt-rev__media-item--video">Video attached</div>`
      );
    });
    return `<div class="tt-rev__media">${items.join('')}</div>`;
  }

  function avatarTone(name) {
    const tones = ['a', 'b', 'c', 'd', 'e'];
    const s = String(name || 'C');
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) hash = (hash + s.charCodeAt(i) * (i + 1)) % tones.length;
    return tones[hash];
  }

  function reviewCard(review, index = 0) {
    const name = escapeHtml(review.reviewer_name || 'Customer');
    const initial = escapeHtml((review.reviewer_initial || name.charAt(0) || 'C').toUpperCase());
    const bodyRaw = stripHtml(review.body_html || review.title || '') || 'Great product.';
    const body = escapeHtml(bodyRaw);
    const title = escapeHtml(review.title || '');
    const date = escapeHtml(formatDate(review.created_at));
    const verified = review.verified_buyer
      ? '<span class="tt-rev__verified"><span aria-hidden="true">✓</span> Verified</span>'
      : '';
    const titleBlock = title ? `<h3 class="tt-rev__title">${title}</h3>` : '';
    const tone = avatarTone(review.reviewer_name || review.uuid || 'C');
    const delay = Math.min(Number(index) || 0, 9);
    const needsClamp = bodyRaw.length > 120;

    return [
      `<article class="tt-rev jdgm-rev" data-review-id="${escapeHtml(review.uuid)}" style="--i:${delay}" data-tone="${tone}">`,
      '  <span class="tt-rev__quote" aria-hidden="true">“</span>',
      '  <span class="tt-rev__shine" aria-hidden="true"></span>',
      '  <header class="tt-rev__header jdgm-rev__header">',
      `    <div class="tt-rev__avatar tt-rev__avatar--${tone}" aria-hidden="true">${initial}</div>`,
      '    <div class="tt-rev__meta">',
      `      <div class="tt-rev__name jdgm-rev__author">${name}</div>`,
      '      <div class="tt-rev__row">' + starsHtml(review.rating, true) + verified + '</div>',
      date ? `      <time class="tt-rev__date jdgm-rev__timestamp">${date}</time>` : '',
      '    </div>',
      '  </header>',
      '  <div class="tt-rev__body jdgm-rev__body">',
      titleBlock,
      `<p class="tt-rev__text${needsClamp ? ' is-clamp' : ''}">${body}</p>`,
      needsClamp
        ? '<button type="button" class="tt-rev__expand" data-tt-rev-expand aria-expanded="false">Read more</button>'
        : '',
      mediaHtml(review),
      '  </div>',
      '</article>',
    ].join('');
  }

  function renderSummary(root, count, average) {
    const summary = root.querySelector('[data-tt-summary]');
    if (!summary || !count) return;
    const avgLabel = average.toFixed(1).replace(/\.0$/, '');
    const avgEl = summary.querySelector('[data-tt-summary-avg]');
    const starsEl = summary.querySelector('[data-tt-summary-stars]');
    const countEl = summary.querySelector('[data-tt-summary-count]');
    if (starsEl) starsEl.innerHTML = starsHtml(average);
    if (countEl) countEl.textContent = `${count} ${count === 1 ? 'review' : 'reviews'} from real customers`;
    summary.hidden = false;
    requestAnimationFrame(() => summary.classList.add('is-in'));

    if (avgEl && !reduce) {
      const target = Number(average) || 0;
      const start = performance.now();
      const dur = 900;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        avgEl.textContent = (target * eased).toFixed(1).replace(/\.0$/, '');
        if (t < 1) requestAnimationFrame(tick);
        else avgEl.textContent = avgLabel;
      };
      requestAnimationFrame(tick);
    } else if (avgEl) {
      avgEl.textContent = avgLabel;
    }
  }

  async function fetchProductReviews(shopDomain, productId) {
    const all = [];
    let page = 1;
    let totalPages = 1;
    let meta = { number_of_reviews: 0, average_rating: 0 };

    while (page <= totalPages && page <= 20) {
      const url = new URL(API);
      url.searchParams.set('url', shopDomain);
      url.searchParams.set('shop_domain', shopDomain);
      url.searchParams.set('platform', 'shopify');
      url.searchParams.set('product_id', String(productId));
      url.searchParams.set('page', String(page));
      url.searchParams.set('per_page', String(PER_PAGE));

      const res = await fetch(url.toString(), { credentials: 'omit' });
      if (!res.ok) break;
      const data = await res.json();
      meta.number_of_reviews = Number(data.number_of_reviews || 0);
      meta.average_rating = Number(data.average_rating || 0);

      const reviews = Array.isArray(data.reviews) ? data.reviews : [];
      all.push(...reviews);

      totalPages = Number(data.pagination?.total_pages || 1);
      if (!reviews.length) break;
      page += 1;
    }

    return { meta, reviews: all };
  }

  async function fetchRatingSummary(shopDomain, productIds) {
    const results = await Promise.all(
      productIds.map(async (productId) => {
        try {
          const url = new URL(API);
          url.searchParams.set('url', shopDomain);
          url.searchParams.set('shop_domain', shopDomain);
          url.searchParams.set('platform', 'shopify');
          url.searchParams.set('product_id', String(productId));
          url.searchParams.set('page', '1');
          url.searchParams.set('per_page', '1');
          const res = await fetch(url.toString(), { credentials: 'omit' });
          if (!res.ok) return null;
          const data = await res.json();
          return {
            count: Number(data.number_of_reviews || 0),
            average: Number(data.average_rating || 0),
          };
        } catch (_err) {
          return null;
        }
      })
    );

    let totalCount = 0;
    let weighted = 0;
    results.forEach((item) => {
      if (!item || !item.count) return;
      totalCount += item.count;
      weighted += item.average * item.count;
    });

    return {
      count: totalCount,
      average: totalCount > 0 ? weighted / totalCount : 0,
    };
  }

  async function fetchAllReviews(shopDomain, productIds) {
    const seen = new Set();
    const reviews = [];
    let totalCount = 0;
    let weighted = 0;

    const batches = await Promise.all(
      productIds.map(async (productId) => {
        try {
          return await fetchProductReviews(shopDomain, productId);
        } catch (_err) {
          return { meta: { number_of_reviews: 0, average_rating: 0 }, reviews: [] };
        }
      })
    );

    batches.forEach(({ meta, reviews: list }) => {
      totalCount += meta.number_of_reviews || list.length;
      weighted += (meta.average_rating || 0) * (meta.number_of_reviews || list.length);
      list.forEach((rev) => {
        const key = rev.uuid || `${rev.reviewer_name}-${rev.created_at}-${rev.body_html}`;
        if (seen.has(key)) return;
        seen.add(key);
        reviews.push(rev);
      });
    });

    reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const avg =
      totalCount > 0
        ? weighted / totalCount
        : reviews.length
          ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length
          : 0;

    return {
      reviews,
      count: Math.max(totalCount, reviews.length),
      average: avg,
    };
  }

  function renderPdpRating(el, count, average) {
    if (!el || !count) return;
    const avgLabel = average.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    const stars = el.querySelector('[data-tt-pdp-stars]');
    const avgEl = el.querySelector('[data-tt-pdp-avg]');
    const countEl = el.querySelector('[data-tt-pdp-count]');
    if (stars) stars.innerHTML = starsHtml(average);
    if (avgEl) avgEl.textContent = avgLabel;
    if (countEl) countEl.textContent = count + (count === 1 ? ' review' : ' reviews');
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-in'));
  }

  function toDirectTmpUrl(url) {
    if (!url) return '';
    if (url.includes('/dl/')) return url;
    return url.replace('https://tmpfiles.org/', 'https://tmpfiles.org/dl/');
  }

  async function uploadFile(file) {
    const body = new FormData();
    body.append('file', file);
    const res = await fetch('https://tmpfiles.org/api/v1/upload', {
      method: 'POST',
      body,
    });
    if (!res.ok) throw new Error('Upload failed');
    const data = await res.json();
    const url = data?.data?.url || '';
    if (!url) throw new Error('Upload failed');
    return toDirectTmpUrl(url);
  }

  function initWriteForm(root) {
    const form = root.querySelector('[data-tt-write-form]');
    if (!form) return;

    const writePanel = root.querySelector('[data-tt-write]');
    const toggleBtn = root.querySelector('[data-tt-write-toggle]');
    const cancelBtn = root.querySelector('[data-tt-write-cancel]');
    const ratingInput = form.querySelector('[data-tt-rating]');
    const stars = [...form.querySelectorAll('[data-star]')];
    const status = form.querySelector('[data-tt-write-status]');
    const submitBtn = form.querySelector('[data-tt-write-submit]');
    const photoInput = form.querySelector('[data-tt-photos]');
    const videoInput = form.querySelector('[data-tt-video]');
    const previews = form.querySelector('[data-tt-media-previews]');
    const shopDomain =
      root.dataset.shopDomain ||
      window.Shopify?.shop ||
      'c0b0gg-y1.myshopify.com';
    const productId = root.dataset.productId || '';
    const productTitle = root.dataset.productTitle || 'this product';

    let photoFiles = [];
    let videoFile = null;
    const objectUrls = [];

    function setWriteOpen(open) {
      if (!writePanel) return;
      const label = toggleBtn?.querySelector('[data-tt-write-label]') || toggleBtn;
      root.classList.toggle('is-writing', open);
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) label.textContent = open ? 'Hide form' : 'Write a review';

      if (open) {
        writePanel.hidden = false;
        writePanel.classList.remove('is-open');
        requestAnimationFrame(() => {
          writePanel.classList.add('is-open');
          writePanel.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
          writePanel.querySelector('input[name="name"]')?.focus();
        });
      } else {
        writePanel.classList.remove('is-open');
        window.setTimeout(() => {
          if (!root.classList.contains('is-writing')) writePanel.hidden = true;
        }, reduce ? 0 : 380);
      }
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        setWriteOpen(Boolean(writePanel?.hidden));
      });
    }
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => setWriteOpen(false));
    }

    function revokeUrls() {
      while (objectUrls.length) {
        try {
          URL.revokeObjectURL(objectUrls.pop());
        } catch (_e) {
          /* ignore */
        }
      }
    }

    function setRating(value) {
      const n = Math.max(1, Math.min(5, Number(value) || 5));
      if (ratingInput) ratingInput.value = String(n);
      stars.forEach((star) => {
        const v = Number(star.dataset.star);
        const on = v <= n;
        star.classList.toggle('is-on', on);
        star.setAttribute('aria-checked', on && v === n ? 'true' : 'false');
        if (on) star.style.setProperty('--pop', String(0.04 * (6 - Math.abs(v - n))));
      });
    }

    setRating(5);

    stars.forEach((star) => {
      star.addEventListener('click', () => setRating(star.dataset.star));
      star.addEventListener('mouseenter', () => {
        const hover = Number(star.dataset.star);
        stars.forEach((s) => s.classList.toggle('is-hover', Number(s.dataset.star) <= hover));
      });
      star.addEventListener('mouseleave', () => {
        stars.forEach((s) => s.classList.remove('is-hover'));
      });
    });

    function setStatus(msg, type) {
      if (!status) return;
      status.hidden = !msg;
      status.textContent = msg || '';
      status.classList.toggle('is-ok', type === 'ok');
      status.classList.toggle('is-err', type === 'err');
    }

    function renderPreviews() {
      if (!previews) return;
      revokeUrls();
      const bits = [];

      photoFiles.forEach((file, index) => {
        const url = URL.createObjectURL(file);
        objectUrls.push(url);
        bits.push(
          `<div class="tt-write__preview" data-kind="photo" data-index="${index}">` +
            `<img src="${url}" alt="">` +
            `<button type="button" class="tt-write__preview-remove" aria-label="Remove photo">×</button>` +
            `</div>`
        );
      });

      if (videoFile) {
        const url = URL.createObjectURL(videoFile);
        objectUrls.push(url);
        bits.push(
          `<div class="tt-write__preview tt-write__preview--video" data-kind="video">` +
            `<video src="${url}" muted playsinline></video>` +
            `<span class="tt-write__preview-tag">Video</span>` +
            `<button type="button" class="tt-write__preview-remove" aria-label="Remove video">×</button>` +
            `</div>`
        );
      }

      previews.innerHTML = bits.join('');
    }

    function tooLarge(file) {
      return file.size > MAX_FILE_MB * 1024 * 1024;
    }

    if (photoInput) {
      photoInput.addEventListener('change', () => {
        const next = [...(photoInput.files || [])];
        next.forEach((file) => {
          if (tooLarge(file)) {
            setStatus('Each file must be under 25 MB.', 'err');
            return;
          }
          if (photoFiles.length >= MAX_PHOTOS) return;
          photoFiles.push(file);
        });
        photoInput.value = '';
        renderPreviews();
      });
    }

    if (videoInput) {
      videoInput.addEventListener('change', () => {
        const file = (videoInput.files || [])[0];
        videoInput.value = '';
        if (!file) return;
        if (tooLarge(file)) {
          setStatus('Each file must be under 25 MB.', 'err');
          return;
        }
        videoFile = file;
        renderPreviews();
      });
    }

    if (previews) {
      previews.addEventListener('click', (event) => {
        const btn = event.target.closest('.tt-write__preview-remove');
        if (!btn) return;
        const card = btn.closest('.tt-write__preview');
        if (!card) return;
        if (card.dataset.kind === 'video') {
          videoFile = null;
        } else {
          const index = Number(card.dataset.index);
          photoFiles = photoFiles.filter((_, i) => i !== index);
        }
        renderPreviews();
      });
    }

    async function openWhatsAppFallback({ name, email, title, body, rating, pictureUrls }) {
      const wa =
        document.querySelector('[data-whatsapp-number]')?.dataset.whatsappNumber ||
        '919039592817';
      const lines = [
        'Hi Mukhlis! I want to share a product review for ' + productTitle + '.',
        '',
        'Name: ' + name,
        'Email: ' + email,
        'Rating: ' + rating + '/5',
        title ? 'Title: ' + title : null,
        '',
        body,
      ].filter(Boolean);

      if (pictureUrls.length) {
        lines.push('', 'Photo links:');
        pictureUrls.forEach((url, i) => lines.push(i + 1 + '. ' + url));
      }
      if (videoFile) {
        lines.push('', 'I also selected a video — please ask me if the file did not attach.');
      }

      const text = lines.join('\n');
      const files = [...photoFiles];
      if (videoFile) files.push(videoFile);

      if (navigator.canShare && files.length && navigator.canShare({ files })) {
        try {
          await navigator.share({
            title: productTitle + ' review',
            text,
            files,
          });
          return 'shared';
        } catch (_err) {
          /* fall through to WhatsApp */
        }
      }

      window.open(
        'https://wa.me/' + String(wa).replace(/\D/g, '') + '?text=' + encodeURIComponent(text),
        '_blank',
        'noopener'
      );
      return 'whatsapp';
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      setStatus('', '');

      const data = new FormData(form);
      const name = String(data.get('name') || '').trim();
      const email = String(data.get('email') || '').trim();
      const title = String(data.get('title') || '').trim();
      const body = String(data.get('body') || '').trim();
      const rating = Number(data.get('rating') || ratingInput?.value || 5);

      if (!name || !email || !body) {
        setStatus('Please fill name, email, and your review.', 'err');
        return;
      }
      if (!productId) {
        setStatus('Could not find this product. Please refresh and try again.', 'err');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setStatus('Please enter a valid email address.', 'err');
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending…';
      }

      let pictureUrls = [];
      try {
        if (photoFiles.length) {
          setStatus('Uploading photos…', '');
          pictureUrls = await Promise.all(photoFiles.map((file) => uploadFile(file)));
        }
      } catch (_err) {
        setStatus('Could not upload photos. You can still send the review via WhatsApp.', 'err');
      }

      const payload = {
        shop_domain: shopDomain,
        platform: 'shopify',
        id: Number(productId),
        name,
        email,
        rating,
        body,
      };
      if (title) payload.title = title;
      if (pictureUrls.length) payload.picture_urls = pictureUrls;

      try {
        const res = await fetch(REVIEW_POST, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        let json = null;
        try {
          json = await res.json();
        } catch (_e) {
          json = null;
        }

        const apiMsg = String(json?.message || json?.error || '');
        const blocked =
          /not allowed|unverified|web reviews/i.test(apiMsg) ||
          (res.status === 422 && /not allowed/i.test(apiMsg));

        if (blocked || !res.ok) {
          await openWhatsAppFallback({ name, email, title, body, rating, pictureUrls });
          const hadMedia = photoFiles.length > 0 || Boolean(videoFile);
          form.reset();
          setRating(5);
          photoFiles = [];
          videoFile = null;
          renderPreviews();
          setStatus(
            hadMedia
              ? 'Opened WhatsApp/share with your review and media. We will publish it after checking.'
              : 'Opened WhatsApp with your review. We will publish it after checking.',
            'ok'
          );
          return;
        }

        form.reset();
        setRating(5);
        photoFiles = [];
        videoFile = null;
        renderPreviews();
        setStatus('Thank you! Your review was submitted and will appear after moderation.', 'ok');
      } catch (err) {
        try {
          await openWhatsAppFallback({ name, email, title, body, rating, pictureUrls });
          form.reset();
          setRating(5);
          photoFiles = [];
          videoFile = null;
          renderPreviews();
          setStatus('Opened WhatsApp with your review so we can publish it for you.', 'ok');
        } catch (_e2) {
          setStatus(err.message || 'Could not submit review. Please try again.', 'err');
        }
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit review';
        }
      }
    });
  }

  function initTtReviews(root) {
    const stage = root.querySelector('[data-tt-reviews-stage]');
    const rail = root.querySelector('[data-tt-reviews-rail]');
    const loading = root.querySelector('[data-tt-reviews-loading]');
    const empty = root.querySelector('[data-tt-reviews-empty]');
    const moreWrap = root.querySelector('[data-tt-more-wrap]');
    const moreBtn = root.querySelector('[data-tt-see-more]');
    const dotsEl = root.querySelector('[data-tt-review-dots]');
    const swipeHint = root.querySelector('[data-tt-swipe-hint]');
    if (!stage || !rail) return;

    initWriteForm(root);

    const shopDomain =
      root.dataset.shopDomain ||
      window.Shopify?.shop ||
      window.location.hostname.replace(/^www\./, '');

    const productIds = (root.dataset.reviewProducts || root.dataset.takmeeliReviewProducts || root.dataset.productId || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    const pageSize = Math.max(1, Number(root.dataset.pageSize) || 10);
    let allReviews = [];
    let shown = 0;
    const isMobile = () => window.matchMedia('(max-width: 649px)').matches;

    if (!productIds.length) {
      if (loading) loading.classList.add('is-hidden');
      if (empty) empty.hidden = false;
      return;
    }

    const markReady = () => {
      root.classList.add('is-ready');
      rail.classList.add('is-ready');
      if (loading) {
        loading.classList.add('is-hidden');
        loading.hidden = true;
        loading.setAttribute('aria-hidden', 'true');
      }
      if (empty) empty.hidden = true;
    };

    const showEmpty = (msg) => {
      if (loading) {
        loading.classList.add('is-hidden');
        loading.hidden = true;
      }
      if (moreWrap) moreWrap.hidden = true;
      if (dotsEl) dotsEl.hidden = true;
      if (swipeHint) swipeHint.hidden = true;
      if (empty) {
        if (msg) empty.textContent = msg;
        empty.hidden = false;
      }
    };

    const moreLabel = moreBtn?.querySelector('[data-tt-see-more-label]') || moreBtn;

    const syncMoreButton = () => {
      if (!moreWrap || !moreBtn) return;
      const remaining = allReviews.length - shown;
      if (remaining <= 0) {
        moreWrap.hidden = true;
        return;
      }
      moreWrap.hidden = false;
      moreBtn.disabled = false;
      moreBtn.classList.remove('is-loading');
      if (moreLabel) {
        moreLabel.textContent = remaining === 1 ? 'See 1 more' : `See more · ${remaining} left`;
      }
    };

    const syncDots = () => {
      if (!dotsEl) return;
      const cards = [...rail.querySelectorAll('.tt-rev')];
      if (!isMobile() || cards.length <= 1) {
        dotsEl.hidden = true;
        dotsEl.innerHTML = '';
        return;
      }
      dotsEl.hidden = false;
      dotsEl.innerHTML = cards
        .map((_, i) => `<button type="button" class="tt-reviews__dot" data-tt-dot="${i}" aria-label="Go to review ${i + 1}"></button>`)
        .join('');
      updateActiveDot();
    };

    const updateActiveDot = () => {
      if (!dotsEl || dotsEl.hidden) return;
      const cards = [...rail.querySelectorAll('.tt-rev')];
      if (!cards.length) return;
      const railRect = rail.getBoundingClientRect();
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((card, i) => {
        const rect = card.getBoundingClientRect();
        const dist = Math.abs(rect.left - railRect.left - 8);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      dotsEl.querySelectorAll('.tt-reviews__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === best);
      });
      cards.forEach((card, i) => card.classList.toggle('is-active-slide', i === best));
    };

    const showSwipeHint = () => {
      if (!swipeHint || reduce || !isMobile()) return;
      const cards = rail.querySelectorAll('.tt-rev');
      if (cards.length < 2) {
        swipeHint.hidden = true;
        return;
      }
      swipeHint.hidden = false;
      window.setTimeout(() => {
        swipeHint.hidden = true;
      }, 2800);
    };

    const revealCards = (cards) => {
      cards.forEach((card, i) => {
        card.style.setProperty('--i', String(i % 10));
        if (reduce) {
          card.classList.add('is-visible');
          return;
        }
        window.setTimeout(() => card.classList.add('is-visible'), 30 + i * 45);
      });
    };

    const renderPage = (append) => {
      const slice = allReviews.slice(shown, shown + pageSize);
      if (!slice.length) {
        syncMoreButton();
        return;
      }
      const startIndex = shown;
      const html = slice.map((review, i) => reviewCard(review, startIndex + i)).join('');
      if (append) rail.insertAdjacentHTML('beforeend', html);
      else rail.innerHTML = html;
      shown += slice.length;
      markReady();
      syncMoreButton();
      const cards = [...rail.querySelectorAll('.tt-rev')].slice(append ? -slice.length : 0);
      revealCards(cards);
      syncDots();
      if (!append) showSwipeHint();
      requestAnimationFrame(updateActiveDot);
    };

    // Rating above price: light page-1 summary only (fast)
    hydratePdpRatingFrom(shopDomain, productIds);

    fetchAllReviews(shopDomain, productIds)
      .then(({ reviews, count, average }) => {
        document.querySelectorAll('[data-tt-pdp-rating]').forEach((el) => {
          renderPdpRating(el, count, average);
        });
        renderSummary(root, count, average);

        if (!reviews.length) {
          showEmpty('No reviews yet. Be the first to share your experience.');
          return;
        }

        allReviews = reviews;
        shown = 0;
        renderPage(false);
      })
      .catch(() => {
        showEmpty('Could not load reviews. Please refresh the page.');
      });

    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        moreBtn.disabled = true;
        moreBtn.classList.add('is-loading');
        if (moreLabel) moreLabel.textContent = 'Loading…';
        const before = shown;
        renderPage(true);
        if (isMobile()) {
          const cards = rail.querySelectorAll('.tt-rev');
          const next = cards[before];
          window.setTimeout(() => {
            next?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
            updateActiveDot();
          }, 60);
        }
      });
    }

    rail.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-tt-rev-expand]');
      if (!btn) return;
      const card = btn.closest('.tt-rev');
      const text = card?.querySelector('.tt-rev__text');
      if (!text) return;
      const willExpand = text.classList.contains('is-clamp');
      text.classList.toggle('is-clamp', !willExpand);
      btn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
      btn.textContent = willExpand ? 'Show less' : 'Read more';
      card?.classList.toggle('is-expanded', willExpand);
    });

    let scrollTick = 0;
    rail.addEventListener(
      'scroll',
      () => {
        if (swipeHint && !swipeHint.hidden) {
          swipeHint.hidden = true;
        }
        window.cancelAnimationFrame(scrollTick);
        scrollTick = window.requestAnimationFrame(updateActiveDot);
      },
      { passive: true }
    );

    if (dotsEl) {
      dotsEl.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-tt-dot]');
        if (!dot) return;
        const index = Number(dot.dataset.ttDot);
        const card = rail.querySelectorAll('.tt-rev')[index];
        card?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
      });
    }

    window.addEventListener(
      'resize',
      () => {
        syncDots();
        updateActiveDot();
      },
      { passive: true }
    );
  }

  async function hydratePdpRatingFrom(shopDomain, productIds) {
    const nodes = [...document.querySelectorAll('[data-tt-pdp-rating]')];
    if (!nodes.length || !productIds.length) return;
    try {
      const { count, average } = await fetchRatingSummary(shopDomain, productIds);
      nodes.forEach((node) => renderPdpRating(node, count, average));
    } catch (_err) {
      /* silent */
    }
  }

  async function hydratePdpRatingAlone() {
    const nodes = [...document.querySelectorAll('[data-tt-pdp-rating]')];
    if (!nodes.length) return;

    const el = nodes[0];
    const shopDomain =
      el.dataset.shopDomain ||
      window.Shopify?.shop ||
      window.location.hostname.replace(/^www\./, '') ||
      'c0b0gg-y1.myshopify.com';
    const productIds = (el.dataset.reviewProducts || el.dataset.takmeeliReviewProducts || el.dataset.productId || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (!productIds.length) return;

    await hydratePdpRatingFrom(shopDomain, productIds);
  }

  function bootTtReviews() {
    // Paint PDP rating as early as possible — do not wait on the full review rail
    hydratePdpRatingAlone();
    document.querySelectorAll('.tt-reviews[data-tt-reviews]').forEach(initTtReviews);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootTtReviews);
  } else {
    bootTtReviews();
  }
})();
